import { useDroppable } from "@dnd-kit/core"
import { useRef, useState } from "react"
import { Dayjs } from "dayjs"
import { DateRangeViewEvent, ConsultationCalendarEvent, TaskCalendarEvent } from "../scheduleApi"
import { EventDisplayInfo } from "../scheduleModel"
import WeekViewEvent from "./WeekViewEvent"
import WeekViewConsultation from "./WeekViewConsultation"
import WeekViewTask from "./WeekViewTask"
import { HOUR_HEIGHT, TOTAL_HOURS, START_HOUR, calculateCombinedColumns, getMinutesFromOffset15, calculateDragSelection, formatTimeFromMinutes } from "./weekViewModel"
import type { SelectionPreview } from "./weekViewModel"

export type Props = {
  day: Dayjs
  events: DateRangeViewEvent[]
  consultations?: ConsultationCalendarEvent[]
  tasks?: TaskCalendarEvent[]
  eventDisplayInfo: Map<number, EventDisplayInfo>
  onEventClick: (event: DateRangeViewEvent) => void
  onConsultationClick?: (consultation: ConsultationCalendarEvent) => void
  onTaskClick?: (event: TaskCalendarEvent) => void
  onEventResize?: (event: DateRangeViewEvent, newDurationMinutes: number) => void
  onConsultationResize?: (consultation: ConsultationCalendarEvent, newDurationMinutes: number) => void
  onTaskResize?: (event: TaskCalendarEvent, newDurationMinutes: number) => void
  onResizingChange?: (isResizing: boolean) => void
  isResizing?: boolean
  topOffset: number
  onCalendarClick: (day: Dayjs, minutes: number) => void
  onDragSelect: (day: Dayjs, startMinutes: number, durationMinutes: number) => void
}

type TimeSlotProps = {
  day: Dayjs
  minutes: number
  isHourLine: boolean
  isResizing?: boolean
  topOffset: number
}

const SLOT_HEIGHT = HOUR_HEIGHT / 2
const DRAG_THRESHOLD = 5

const TimeSlot = ({ day, minutes, isHourLine, isResizing, topOffset }: TimeSlotProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `slot-${day.format('YYYY-MM-DD')}-${minutes}`,
    data: { date: day, minutes },
  })

  const showHighlight = isOver && !isResizing

  return (
    <div
      ref={setNodeRef}
      style={{
        position: 'absolute',
        top: ((minutes - START_HOUR * 60) / 60) * HOUR_HEIGHT - topOffset,
        left: 0,
        right: 0,
        height: SLOT_HEIGHT,
        borderBottom: isHourLine ? '1px solid #e8e8e8' : '1px solid #f5f5f5',
        boxSizing: 'border-box',
        backgroundColor: showHighlight ? '#e6f7ff' : 'transparent',
      }}
    />
  )
}

const isEventTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null
  if (!el) {
    return false
  }
  return !!el.closest('[role="button"]')
}

const WeekViewDay = ({ day, events, consultations = [], tasks = [], eventDisplayInfo, onEventClick, onConsultationClick, onTaskClick, onEventResize, onConsultationResize, onTaskResize, onResizingChange, isResizing, topOffset, onCalendarClick, onDragSelect }: Props) => {
  const { eventColumns, consultationColumns, taskColumns } = calculateCombinedColumns(events, consultations, tasks)
  const dragStateRef = useRef<{ startY: number; startMinutes: number; isDragging: boolean } | null>(null)
  const [selectionPreview, setSelectionPreview] = useState<SelectionPreview | null>(null)

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isEventTarget(e.target)) {
      return
    }
    if (e.button !== 0) {
      return
    }

    const rect = e.currentTarget.getBoundingClientRect()
    const offsetY = e.clientY - rect.top + topOffset
    const minutes = getMinutesFromOffset15(offsetY)

    dragStateRef.current = {
      startY: e.clientY,
      startMinutes: minutes,
      isDragging: false,
    }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragStateRef.current) {
      return
    }

    const distanceMoved = Math.abs(e.clientY - dragStateRef.current.startY)
    if (distanceMoved >= DRAG_THRESHOLD) {
      dragStateRef.current.isDragging = true

      const rect = e.currentTarget.getBoundingClientRect()
      const offsetY = e.clientY - rect.top + topOffset
      const currentMinutes = getMinutesFromOffset15(offsetY)

      setSelectionPreview(calculateDragSelection(dragStateRef.current.startMinutes, currentMinutes))
    }
  }

  const handleMouseUp = (_e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragStateRef.current) {
      return
    }

    const { startMinutes, isDragging } = dragStateRef.current

    if (isDragging && selectionPreview) {
      const duration = selectionPreview.endMinutes - selectionPreview.startMinutes
      onDragSelect(day, selectionPreview.startMinutes, duration)
    } else if (!isDragging) {
      onCalendarClick(day, startMinutes)
    }

    dragStateRef.current = null
    setSelectionPreview(null)
  }

  const handleMouseLeave = () => {
    dragStateRef.current = null
    setSelectionPreview(null)
  }

  const effectiveStartHour = topOffset / HOUR_HEIGHT
  const slots: { minutes: number; isHourLine: boolean }[] = []
  for (let hour = effectiveStartHour; hour < TOTAL_HOURS; hour++) {
    const baseMinutes = (START_HOUR + hour) * 60
    slots.push({ minutes: baseMinutes, isHourLine: false })
    slots.push({ minutes: baseMinutes + 30, isHourLine: true })
  }

  return (
    <div
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative',
        backgroundColor: '#fff',
        height: '100%',
        userSelect: 'none',
      }}
    >
      {slots.map((slot) => (
        <TimeSlot
          key={slot.minutes}
          day={day}
          minutes={slot.minutes}
          isHourLine={slot.isHourLine}
          isResizing={isResizing}
          topOffset={topOffset}
        />
      ))}

      {selectionPreview && (
        <div
          style={{
            position: 'absolute',
            top: ((selectionPreview.startMinutes - START_HOUR * 60) / 60) * HOUR_HEIGHT - topOffset,
            left: 0,
            right: 0,
            height: ((selectionPreview.endMinutes - selectionPreview.startMinutes) / 60) * HOUR_HEIGHT,
            backgroundColor: 'rgba(24, 144, 255, 0.15)',
            border: '1px solid #1890ff',
            borderRadius: 4,
            zIndex: 5,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: 4,
          }}
        >
          <span style={{ fontSize: 11, color: '#1890ff', fontWeight: 500 }}>
            {formatTimeFromMinutes(selectionPreview.startMinutes)} – {formatTimeFromMinutes(selectionPreview.endMinutes)}
          </span>
        </div>
      )}

      {eventColumns.map((eventColumn) => {
        const info = eventDisplayInfo.get(eventColumn.event.id)
        return (
          <WeekViewEvent
            key={eventColumn.event.id}
            eventColumn={eventColumn}
            displayInfo={info}
            onEventClick={onEventClick}
            onResize={info?.isDiscreteVisit ? undefined : onEventResize}
            onResizingChange={info?.isDiscreteVisit ? undefined : onResizingChange}
            topOffset={topOffset}
          />
        )
      })}

      {consultationColumns.map((consultationColumn) => (
        <WeekViewConsultation
          key={consultationColumn.consultation.id}
          consultationColumn={consultationColumn}
          onConsultationClick={onConsultationClick}
          onResize={onConsultationResize}
          onResizingChange={onResizingChange}
          topOffset={topOffset}
        />
      ))}

      {taskColumns.map((taskColumn) => (
        <WeekViewTask
          key={taskColumn.event.id}
          taskColumn={taskColumn}
          onTaskClick={onTaskClick}
          onResize={onTaskResize}
          onResizingChange={onResizingChange}
          topOffset={topOffset}
        />
      ))}
    </div>
  )
}

export default WeekViewDay
